// reviewService.js
// Purpose: Provide weekly review data (derived from chat sessions/messages) with permanent cache + incremental sync.
// Notes:
// - If a dedicated "reviews" table exists, we prefer reading from it (and enrich with cached counts).
//   Otherwise, we derive weekly summaries from existing chat sessions in cache.
// - We follow the same caching patterns used by other services: permanent cache with incremental updates.
// - Cache key used: 'reviews_weekly'. The cache content includes a baseline of computed weekly summaries
//   plus a reference to the last chat sessions sync timestamp used to compute it.

import { cacheManager, persistentCache } from '../cacheManager';
import * as chatService from './chatService';
import { supabase } from '../../config/supabase';
import logger from '../../utils/logger';
import { isEnabled } from '../../utils/flags';
import { ReviewRowSchema, WeeklySummarySchema } from '../../lib/schemas/review';

/**
 * Compute ISO week start (Monday 00:00:00) for a given Date
 * @param {Date} date
 * @returns {string} ISO string for week start
 */
function getIsoWeekStart(date) {
  const d = new Date(date);
  const day = (d.getUTCDay() + 6) % 7; // convert Sunday(0) -> 6
  d.setUTCHours(0, 0, 0, 0);
  d.setUTCDate(d.getUTCDate() - day);
  return d.toISOString();
}

/**
 * Compute ISO week end (Sunday 23:59:59.999) from week start
 * @param {string} weekStartIso
 * @returns {string}
 */
function getIsoWeekEnd(weekStartIso) {
  const d = new Date(weekStartIso);
  d.setUTCDate(d.getUTCDate() + 6);
  d.setUTCHours(23, 59, 59, 999);
  return d.toISOString();
}

/**
 * Transform chat sessions to weekly summaries using cached data only (no network).
 * - Groups sessions by `updated_at` week
 * - For each week: { weekStart, weekEnd, chatCount, messageCount }
 * - messageCount is derived from cached messages per session if present; omitted if not available
 * @param {any[]} sessions
 * @param {string} userId
 * @returns {Array<{weekStart: string, weekEnd: string, chatCount: number, messageCount?: number}>}
 */
function aggregateWeeklyFromSessions(sessions, userId) {
  const byWeek = new Map();
  for (const s of sessions || []) {
    const updatedAt = s?.updated_at || s?.created_at || new Date().toISOString();
    const weekStart = getIsoWeekStart(new Date(updatedAt));
    const entry = byWeek.get(weekStart) || {
      weekStart,
      weekEnd: getIsoWeekEnd(weekStart),
      chatCount: 0,
      messageCount: 0,
    };
    entry.chatCount += 1;

    // Try to read cached messages length for this session without fetching from network
    try {
      const cacheKey = `messages_${s?.id}`;
      const cachedMsgs = /** @type {any[]|undefined} */ (cacheManager.get(cacheKey, userId));
      if (Array.isArray(cachedMsgs)) {
        entry.messageCount = (entry.messageCount || 0) + cachedMsgs.length;
      }
    } catch (e) {
      // Best-effort only; keep quiet if cache missing
    }
    byWeek.set(weekStart, entry);
  }
  // Sort by weekStart desc (most recent first)
  return Array.from(byWeek.values()).sort((a, b) => (a.weekStart < b.weekStart ? 1 : -1));
}

/**
 * Get weekly reviews for a user.
 * Strategy:
 * - If cached 'reviews_weekly' exists, return immediately and kick off a background recompute when chats have a newer lastSync.
 * - If no cache or forceRefresh true: fetch chats (service handles incremental/full) and compute baseline.
 * @param {string} userId
 * @param {{ forceRefresh?: boolean, signal?: AbortSignal }} [options]
 * @returns {Promise<Array<{weekStart: string, weekEnd: string, chatCount?: number, messageCount?: number}>>}
 */
export async function getWeeklyReviews(userId, { forceRefresh = false, signal } = {}) {
  if (!userId) {
    logger.warn('⚠️ reviewService.getWeeklyReviews: No userId provided');
    return [];
  }

  const cacheKey = 'reviews_weekly';

  // Happy path: use cached weekly reviews and optionally refresh in background
  if (!forceRefresh && cacheManager.hasData(cacheKey, userId)) {
    const cached = /** @type {any[]} */ (cacheManager.get(cacheKey, userId) || []);
    // Under realtime-first (FF_SYNC_V2), avoid network recompute here; rely on
    // realtime-maintained caches. Only recompute if forceRefresh is requested.
    if (!isEnabled('FF_SYNC_V2')) {
      // Legacy path: background refresh if chats advanced
      refreshWeeklyIfChatsAdvanced(userId, cacheKey, signal).catch((e) =>
        logger.warn('⚠️ reviewService.getWeeklyReviews: Background refresh failed', e),
      );
    }
    return cached;
  }

  // Compute fresh baseline from chat sessions
  return await performWeeklyBaselineComputation(userId, cacheKey, signal);
}

/**
 * Recompute weekly reviews when chat sessions lastSync has advanced beyond the stored baseline used in 'reviews_weekly'.
 * Encodes the chat lastSync into the reviews cache entry for comparison.
 * @param {string} userId
 * @param {string} cacheKey
 * @param {AbortSignal} [signal]
 */
async function refreshWeeklyIfChatsAdvanced(userId, cacheKey, signal) {
  try {
    // Read current reviews cache entry directly from persistentCache to access metadata fields
    const reviewsEntry = /** @type {any} */ (persistentCache._getCacheEntry(cacheKey, userId));
    const currentReviewsBaseline = reviewsEntry?.sourceLastSync || null;

    const chatsLastSync = cacheManager.getLastSyncTimestamp('chats', userId);
    if (chatsLastSync && chatsLastSync !== currentReviewsBaseline) {
      logger.info('🔄 reviewService: Chats advanced since last weekly baseline, recomputing...');
      await performWeeklyBaselineComputation(userId, cacheKey, signal);
    } else {
      logger.debug('✅ reviewService: Weekly baseline up-to-date with chats');
    }
  } catch (error) {
    logger.warn('⚠️ reviewService.refreshWeeklyIfChatsAdvanced: Error', error);
  }
}

/**
 * Compute baseline weekly summaries from chat sessions and store in cache with a reference to chats lastSync.
 * @param {string} userId
 * @param {string} cacheKey
 * @param {AbortSignal} [signal]
 */
async function performWeeklyBaselineComputation(userId, cacheKey, signal) {
  logger.info('🧮 reviewService: Computing weekly baseline from chat sessions');
  // First, try to fetch from the dedicated reviews table if present.
  // In realtime-first mode (FF_SYNC_V2), avoid network reads unless explicitly
  // requested via forceRefresh (the caller gates this by skipping recompute).
  const fromTable = isEnabled('FF_SYNC_V2')
    ? []
    : await fetchWeeklyFromReviewsTable(userId, signal);
  const aggregated =
    Array.isArray(fromTable) && fromTable.length > 0
      ? fromTable
      : (() => {
          // Fallback: derive from chat sessions; chatService implements permanent+incremental strategy
          return aggregateWeeklyFromSessions(
            /** @type {any[]} */ (cacheManager.get('chats', userId) || []),
            userId,
          );
        })();

  // Use the current chats lastSync to stamp the source baseline
  // Prefer reviews table freshness when available; otherwise fallback to chats lastSync
  const reviewsFreshness =
    Array.isArray(fromTable) && fromTable.length > 0
      ? fromTable[0]?.created_at || new Date().toISOString()
      : cacheManager.getLastSyncTimestamp('chats', userId) || new Date().toISOString();

  // Write to cache while preserving our additional metadata fields
  const entry = {
    data: aggregated,
    lastFetch: Date.now(),
    lastSync: new Date().toISOString(),
    source: Array.isArray(fromTable) && fromTable.length > 0 ? 'reviews_table' : 'chats',
    sourceLastSync: reviewsFreshness,
  };
  persistentCache._setCacheEntry(cacheKey, userId, entry);
  cacheManager.set(cacheKey, aggregated, userId);
  logger.info('✅ reviewService: Weekly baseline computed and cached:', aggregated.length, 'weeks');
  return aggregated;
}

/**
 * Read weekly reviews directly from Supabase 'reviews' table (timeframe_mode='last_7_days')
 * and enrich each row with chatCount/messageCount based on cached chats/messages within the window.
 * This avoids extra network calls beyond the table query.
 * @param {string} userId
 * @param {AbortSignal} [signal]
 * @returns {Promise<Array<{weekStart: string, weekEnd: string, chatCount?: number, messageCount?: number, tasksTotal?: number, tasksDone?: number, completionRate?: number, overdueCount?: number, perGoal?: any, summaryText?: string, created_at?: string}>>}
 */
async function fetchWeeklyFromReviewsTable(userId, signal) {
  try {
    let q = supabase
      .from('reviews')
      .select(
        'id, user_id, timeframe_mode, timezone_created_in, window_from, window_to, tasks_total, tasks_done, completion_rate, overdue_count, per_goal, summary_text, created_at',
      )
      .eq('user_id', userId)
      .eq('timeframe_mode', 'last_7_days')
      .order('window_to', { ascending: false });
    if (signal) q = q.abortSignal(signal);
    const { data, error } = await q;
    if (error) {
      logger.warn('⚠️ reviewService.fetchWeeklyFromReviewsTable: Supabase error', error);
      return [];
    }
    if (!Array.isArray(data) || data.length === 0) return [];

    // Validate raw review rows (non-blocking)
    const validatedRows = data.map((row) => {
      const res = ReviewRowSchema.safeParse(row);
      if (!res.success) {
        logger.warn(
          'reviewService.fetchWeeklyFromReviewsTable: ReviewRow validation failed (non-blocking)',
          {
            issues: res.error.issues?.slice(0, 3),
          },
        );
        return row;
      }
      return res.data;
    });

    // Enrich counts from cached chats/messages without network
    const cachedChats = /** @type {any[]} */ (cacheManager.get('chats', userId) || []);
    const mapped = validatedRows.map((row) => {
      const fromIso = row.window_from;
      const toIso = row.window_to;
      const from = new Date(fromIso).getTime();
      const to = new Date(toIso).getTime();
      let chatCount = 0;
      let messageCount = 0;
      for (const s of cachedChats) {
        const ts = new Date(s?.updated_at || s?.created_at || row.created_at).getTime();
        if (ts >= from && ts <= to) {
          chatCount += 1;
          const msgs = /** @type {any[]|undefined} */ (
            cacheManager.get(`messages_${s?.id}`, userId)
          );
          if (Array.isArray(msgs)) messageCount += msgs.length;
        }
      }

      return {
        weekStart: fromIso,
        weekEnd: toIso,
        chatCount: chatCount || undefined,
        messageCount: messageCount || undefined,
        tasksTotal: row.tasks_total,
        tasksDone: row.tasks_done,
        completionRate: row.completion_rate,
        overdueCount: row.overdue_count,
        perGoal: row.per_goal,
        summaryText: row.summary_text,
        created_at: row.created_at,
      };
    });

    // Validate mapped weekly summaries (non-blocking)
    return mapped.map((r) => {
      const res = WeeklySummarySchema.safeParse(r);
      if (!res.success) {
        logger.warn(
          'reviewService.fetchWeeklyFromReviewsTable: WeeklySummary validation failed (non-blocking)',
          {
            issues: res.error.issues?.slice(0, 3),
          },
        );
        return r;
      }
      return res.data;
    });
  } catch (e) {
    logger.warn('⚠️ reviewService.fetchWeeklyFromReviewsTable: Unexpected error', e);
    return [];
  }
}

/**
 * Get generic reviews (all rows) for a user from Supabase 'reviews' table.
 * Uses cache key 'reviews_generic' and enriches rows with chat/message counts when windows are provided.
 * @param {string} userId
 * @param {{ forceRefresh?: boolean, signal?: AbortSignal }} [options]
 * @returns {Promise<Array<{ id: string, user_id: string, timeframe_mode?: string|null, timezone_created_in?: string|null, window_from?: string|null, window_to?: string|null, tasks_total?: number|null, tasks_done?: number|null, completion_rate?: number|null, overdue_count?: number|null, per_goal?: any, summary_text?: string|null, created_at: string, chatCount?: number, messageCount?: number }>>}
 */
export async function getGenericReviews(userId, { forceRefresh = false, signal } = {}) {
  if (!userId) {
    logger.warn('⚠️ reviewService.getGenericReviews: No userId provided');
    return [];
  }

  const cacheKey = 'reviews_generic';

  // Serve from cache if available and not forcing refresh
  if (!forceRefresh && cacheManager.hasData(cacheKey, userId)) {
    // Debug: trace cache hit length and metadata
    const viaManager = /** @type {any[]|null} */ (cacheManager.get(cacheKey, userId));
    if (Array.isArray(viaManager)) {
      logger.debug(
        '🗄️ reviewService.getGenericReviews: Cache hit (manager) for',
        cacheKey,
        'user',
        userId,
        'len=',
        viaManager.length,
      );
      return viaManager;
    }

    // Repair path: persistent entry may exist without userId (legacy). Rehydrate then return.
    try {
      const entry = /** @type {any} */ (persistentCache._getCacheEntry(cacheKey, userId));
      const raw = Array.isArray(entry?.data) ? entry.data : null;
      if (raw && raw.length > 0) {
        logger.info(
          '🛠️ reviewService.getGenericReviews: Repairing legacy cache entry missing userId; re-setting via cacheManager.set()',
        );
        cacheManager.set(cacheKey, raw, userId);
        return raw;
      }
      logger.debug(
        '🧹 reviewService.getGenericReviews: hasData=true but manager miss and no usable raw data; will fetch fresh',
      );
    } catch (e) {
      logger.warn('⚠️ reviewService.getGenericReviews: Cache repair attempt failed', e);
    }
    // Fall through to fresh fetch
  }

  try {
    // Debug: trace query build and options
    logger.info('🔎 reviewService.getGenericReviews: Querying Supabase', {
      userId,
      forceRefresh,
      hasAbortSignal: Boolean(signal),
    });

    let q = supabase
      .from('reviews')
      .select(
        'id, user_id, timeframe_mode, timezone_created_in, window_from, window_to, tasks_total, tasks_done, completion_rate, overdue_count, per_goal, summary_text, created_at',
      )
      .eq('user_id', userId)
      .order('created_at', { ascending: false });
    if (signal) q = q.abortSignal(signal);
    const { data, error } = await q;
    if (error) {
      // Treat aborted requests as benign cancellations (Supabase may surface code '20' or message contains 'AbortError')
      const isAbort =
        /** @type {any} */ (error)?.name === 'AbortError' ||
        /** @type {any} */ (error)?.code === '20' ||
        /AbortError/i.test(String(/** @type {any} */ (error)?.message || ''));
      if (isAbort) {
        logger.debug('⛔ reviewService.getGenericReviews: Request aborted, ignoring');
        return [];
      }
      logger.warn('⚠️ reviewService.getGenericReviews: Supabase error', error);
      // Surface non-abort errors to callers so UI can show it during debugging
      throw error;
    }

    const rows = Array.isArray(data) ? data : [];
    // Validate rows (non-blocking)
    const validatedRows = rows.map((row) => {
      const res = ReviewRowSchema.safeParse(row);
      if (!res.success) {
        logger.warn('reviewService.getGenericReviews: ReviewRow validation failed (non-blocking)', {
          issues: res.error.issues?.slice(0, 3),
        });
        return row;
      }
      return res.data;
    });
    logger.info('🧾 reviewService.getGenericReviews: rows fetched:', rows.length);
    if (rows.length === 0) {
      // Helpful hint during debugging if zero rows are returned
      logger.debug(
        'ℹ️ reviewService.getGenericReviews: No rows for user. Check RLS policies, user_id match, or table contents.',
      );

      // Additional diagnostics: check if table has any rows in this environment
      try {
        const { count: totalCount, error: countErr } = await supabase
          .from('reviews')
          .select('id', { count: 'exact', head: true });
        if (countErr) {
          logger.warn('⚠️ reviewService.getGenericReviews: Count query error', countErr);
        } else {
          logger.info('📊 reviewService.getGenericReviews: reviews table total rows:', totalCount);
        }

        // Fetch a small sample of rows to inspect user_ids present
        const { data: sample, error: sampleErr } = await supabase
          .from('reviews')
          .select('id,user_id,created_at')
          .order('created_at', { ascending: false })
          .limit(3);
        if (sampleErr) {
          logger.warn('⚠️ reviewService.getGenericReviews: Sample query error', sampleErr);
        } else {
          logger.info(
            '🧪 reviewService.getGenericReviews: Sample rows user_ids:',
            (sample || []).map((r) => r.user_id),
          );
        }
      } catch (diagErr) {
        logger.warn('⚠️ reviewService.getGenericReviews: Diagnostics failed', diagErr);
      }
    }

    // Best-effort enrichment using cached chats/messages when window range is present
    const cachedChats = /** @type {any[]} */ (cacheManager.get('chats', userId) || []);
    const enriched = validatedRows.map((row) => {
      let chatCount = undefined;
      let messageCount = undefined;
      if (row.window_from && row.window_to) {
        const from = new Date(row.window_from).getTime();
        const to = new Date(row.window_to).getTime();
        let cc = 0;
        let mc = 0;
        for (const s of cachedChats) {
          const ts = new Date(s?.updated_at || s?.created_at || row.created_at).getTime();
          if (ts >= from && ts <= to) {
            cc += 1;
            const msgs = /** @type {any[]|undefined} */ (
              cacheManager.get(`messages_${s?.id}`, userId)
            );
            if (Array.isArray(msgs)) mc += msgs.length;
          }
        }
        chatCount = cc || undefined;
        messageCount = mc || undefined;
      }

      return {
        ...row,
        chatCount,
        messageCount,
      };
    });

    // Cache result with lastSync metadata
    const entry = {
      data: enriched,
      lastFetch: Date.now(),
      lastSync: new Date().toISOString(),
      source: 'reviews_table',
    };
    persistentCache._setCacheEntry(cacheKey, userId, entry);
    cacheManager.set(cacheKey, enriched, userId);
    logger.debug('💾 reviewService.getGenericReviews: Cached enriched reviews', {
      len: Array.isArray(enriched) ? enriched.length : 0,
    });

    return enriched;
  } catch (e) {
    const isAbort =
      /** @type {any} */ (e)?.name === 'AbortError' ||
      /** @type {any} */ (e)?.code === '20' ||
      /AbortError/i.test(String(/** @type {any} */ (e)?.message || ''));
    if (isAbort) {
      logger.debug('⛔ reviewService.getGenericReviews: Caught abort in catch, ignoring');
      return [];
    }
    logger.warn('⚠️ reviewService.getGenericReviews: Unexpected error', e);
    return [];
  }
}

export default {
  getWeeklyReviews,
  getGenericReviews,
};
